<script setup>
import { ref, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Copy, Check, Share2, ExternalLink } from 'lucide-vue-next'
import { useNotifications } from '../../composables/useNotifications'
import { getMirroredGradient } from '../../config/cardStyles'

const { t: $t } = useI18n()
const { showSuccess, showError } = useNotifications()

const props = defineProps({
  claimUrl: {
    type: String,
    required: true
  },
  emojiPin: {
    type: String,
    required: true
  },
  walletAddress: {
    type: String,
    required: true
  },
  bgStyle: {
    type: Number,
    default: 0
  },
  showConfirmation: {
    type: Boolean,
    default: false
  },
  showInstructions: {
    type: Boolean,
    default: true
  },
  isCardBack: {
    type: Boolean,
    default: false
  }
})

// Mirrored gradient for card back
const cardBackBackground = computed(() => {
  return getMirroredGradient(props.bgStyle)
})

const emit = defineEmits(['confirmation-change'])

// Confirmation checkbox
const savedConfirmation = ref(false)

const handleConfirmationChange = () => {
  emit('confirmation-change', savedConfirmation.value)
}

// Copy URL handler
const copiedUrl = ref(false)
let copiedUrlTimeout = null

async function handleCopyUrl() {
  if (!props.claimUrl) return

  try {
    await navigator.clipboard.writeText(props.claimUrl)
    copiedUrl.value = true
    showSuccess($t('common.copied', 'Copied!'))

    if (copiedUrlTimeout) clearTimeout(copiedUrlTimeout)
    copiedUrlTimeout = setTimeout(() => {
      copiedUrl.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy claim URL:', err)
    showError($t('share.copyFailed', 'Failed to copy'))
  }
}

// Copy PIN handler
const copiedPin = ref(false)
let copiedPinTimeout = null

async function handleCopyPin() {
  if (!props.emojiPin) return

  try {
    await navigator.clipboard.writeText(props.emojiPin)
    copiedPin.value = true
    showSuccess($t('common.copied', 'Copied!'))

    if (copiedPinTimeout) clearTimeout(copiedPinTimeout)
    copiedPinTimeout = setTimeout(() => {
      copiedPin.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy emoji PIN:', err)
    showError($t('share.copyFailed', 'Failed to copy'))
  }
}

// Copy all instructions
const copiedAll = ref(false)
let copiedAllTimeout = null

async function handleCopyAll() {
  if (!props.claimUrl || !props.emojiPin) return

  const fullInstructions = `🎁 Crypto Gift Card - Claim Instructions

⚠️ IMPORTANT: Save this information securely!
If you lose the link and PIN, you will never be able to access the funds.

📎 Claim Link:
${props.claimUrl}

🔐 PIN Code:
${props.emojiPin}

Instructions:
1. Open the claim link
2. Enter the PIN code
3. Follow the instructions to withdraw your funds

⚠️ For security, share the link and PIN through different messaging apps.`

  try {
    await navigator.clipboard.writeText(fullInstructions)
    copiedAll.value = true
    showSuccess($t('share.allCopied', 'All instructions copied!'))

    if (copiedAllTimeout) clearTimeout(copiedAllTimeout)
    copiedAllTimeout = setTimeout(() => {
      copiedAll.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy all instructions:', err)
    showError($t('share.copyFailed', 'Failed to copy'))
  }
}

// Save to device
async function handleSaveToDevice() {
  if (!props.claimUrl || !props.emojiPin) return

  const fullInstructions = `🎁 Crypto Gift Card - Claim Instructions

⚠️ IMPORTANT: Save this information securely!
If you lose the link and PIN, you will never be able to access the funds.

📎 Claim Link:
${props.claimUrl}

🔐 PIN Code:
${props.emojiPin}

Instructions:
1. Open the claim link
2. Enter the PIN code
3. Follow the instructions to withdraw your funds

⚠️ For security, share the link and PIN through different messaging apps.`

  try {
    // Try native share first (mobile)
    if (navigator.share && navigator.canShare({ text: fullInstructions })) {
      await navigator.share({
        title: '🎁 Crypto Gift Card',
        text: fullInstructions
      })
      showSuccess($t('share.saved', 'Saved!'))
    } else {
      // Fallback: download as text file
      const blob = new Blob([fullInstructions], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `crypto-gift-card-${Date.now()}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      showSuccess($t('share.saved', 'Saved to Downloads!'))
    }
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.error('Failed to save:', err)
      showError($t('share.saveFailed', 'Failed to save'))
    }
  }
}

// Preview claim page
function handlePreviewClaimPage() {
  if (!props.claimUrl) return
  window.open(props.claimUrl, '_blank', 'noopener,noreferrer')
}

// Cleanup timeouts on unmount
onUnmounted(() => {
  if (copiedUrlTimeout) clearTimeout(copiedUrlTimeout)
  if (copiedPinTimeout) clearTimeout(copiedPinTimeout)
  if (copiedAllTimeout) clearTimeout(copiedAllTimeout)
})

// Expose state for parent components
defineExpose({
  savedConfirmation,
  copiedAll
})
</script>

<template>
  <!-- Card Back Mode: Fixed aspect ratio card with gradient -->
  <div
    v-if="isCardBack"
    class="card-back-container"
    :style="{ background: cardBackBackground }"
  >
    <div class="card-back-content">
      <!-- Claim Link -->
      <div class="detail-row-back">
        <div class="detail-label-back">{{ $t('share.claimLink', 'CLAIM LINK') }}</div>
        <div class="detail-value-row-back">
          <div class="detail-value-back">{{ claimUrl }}</div>
          <button
            type="button"
            class="btn-action-back btn-open"
            @click="handlePreviewClaimPage"
            :title="$t('common.open', 'Open')"
          >
            <ExternalLink :size="18" />
          </button>
          <button
            type="button"
            class="btn-action-back"
            :class="{ 'btn-copied': copiedUrl }"
            @click="handleCopyUrl"
            :title="$t('common.copy', 'Copy')"
          >
            <Check v-if="copiedUrl" :size="18" />
            <Copy v-else :size="18" />
          </button>
        </div>
      </div>

      <!-- PIN Code -->
      <div class="detail-row-back">
        <div class="detail-label-back">{{ $t('share.pinCode', 'PIN CODE') }}</div>
        <div class="detail-value-row-back">
          <div class="pin-display-back">
            <span class="pin-emojis-back">{{ emojiPin }}</span>
          </div>
          <button
            type="button"
            class="btn-action-back"
            :class="{ 'btn-copied': copiedPin }"
            @click="handleCopyPin"
            :title="$t('common.copy', 'Copy')"
          >
            <Check v-if="copiedPin" :size="18" />
            <Copy v-else :size="18" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Normal Mode: Full instructions layout -->
  <div v-else class="claim-instructions-card">
    <!-- Security Warning (Animated) -->
    <div class="security-warning-animated">
      <div class="warning-emoji">⚠️</div>
      <p class="warning-text">
        {{ $t('share.criticalWarning', 'If you lose the link and the PIN you will never be able to access the funds on this check') }}
      </p>
    </div>

    <!-- Gift Card Details Card -->
    <div class="gift-card-details">
      <div class="details-header">
        <span class="header-icon">🎁</span>
        <span class="header-text">{{ $t('share.giftCardDetails', 'Gift Card Details') }}</span>
      </div>

      <!-- Claim Link -->
      <div class="detail-row">
        <div class="detail-label">{{ $t('share.claimLink', 'Claim Link') }}</div>
        <div class="detail-value-row">
          <div class="detail-value">{{ claimUrl }}</div>
          <button
            type="button"
            class="btn-copy-inline"
            :class="{ 'btn-copied': copiedUrl }"
            @click="handleCopyUrl"
            :title="$t('common.copy', 'Copy')"
          >
            <Check v-if="copiedUrl" :size="16" />
            <Copy v-else :size="16" />
          </button>
        </div>
      </div>

      <!-- PIN Code -->
      <div class="detail-row">
        <div class="detail-label">{{ $t('share.pinCode', 'PIN Code') }}</div>
        <div class="detail-value-row">
          <div class="pin-display">
            <span class="pin-emojis">{{ emojiPin }}</span>
          </div>
          <button
            type="button"
            class="btn-copy-inline"
            :class="{ 'btn-copied': copiedPin }"
            @click="handleCopyPin"
            :title="$t('common.copy', 'Copy')"
          >
            <Check v-if="copiedPin" :size="16" />
            <Copy v-else :size="16" />
          </button>
        </div>
      </div>
    </div>

    <!-- Backup Section -->
    <div class="backup-section">
      <div class="backup-header">
        <span class="backup-icon">💾</span>
        <span class="backup-title">{{ $t('share.backupTitle', 'Backup Gift Card Details') }}</span>
      </div>
      <p class="backup-description">
        {{ $t('share.backupDescription', 'Save these credentials to share with the recipient later') }}
      </p>

      <!-- Backup Actions -->
      <div class="backup-actions">
        <button
          type="button"
          class="btn-backup btn-copy-all"
          :class="{ 'btn-copied': copiedAll }"
          @click="handleCopyAll"
        >
          <Check v-if="copiedAll" :size="18" />
          <Copy v-else :size="18" />
          <span>{{ copiedAll ? $t('share.copied', 'Copied!') : $t('share.copyAll', 'Copy All') }}</span>
        </button>

        <button
          type="button"
          class="btn-backup btn-save-device"
          @click="handleSaveToDevice"
        >
          <Share2 :size="18" />
          <span>{{ $t('share.saveToDevice', 'Save to Device') }}</span>
        </button>
      </div>
    </div>

    <!-- How to Share Instructions (only in share modal) -->
    <div v-if="showInstructions" class="info-section">
      <p class="info-text">
        {{ $t('share.instructions', 'Send the link and PIN through different messaging apps for better security. The recipient will need both to access the funds.') }}
      </p>

      <!-- Preview Button -->
      <button
        type="button"
        class="btn-preview"
        @click="handlePreviewClaimPage"
      >
        <ExternalLink :size="16" />
        <span>{{ $t('share.previewPage', 'Preview Claim Page') }}</span>
      </button>
    </div>

    <!-- Confirmation Checkbox (only in wizard step 3) -->
    <!-- TEMPORARILY DISABLED: Checkbox confirmation -->
    <!--
    <div v-if="showConfirmation" class="confirmation-section">
      <label class="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          v-model="savedConfirmation"
          @change="handleConfirmationChange"
          class="checkbox"
        />
        <span class="text-sm text-gray-700">
          {{ $t('share.confirmationSaved', 'I have saved the gift card details') }}
        </span>
      </label>
    </div>
    -->
  </div>
</template>

<style scoped>
/* Card Back Mode - Fixed aspect ratio container */
.card-back-container {
  @apply w-full rounded-xl overflow-hidden relative;
  aspect-ratio: 1.586;
  /* Same aspect ratio as GiftCard */
}

.card-back-content {
  @apply absolute inset-0 overflow-y-auto overflow-x-hidden;
  @apply p-6 flex flex-col gap-6 justify-center;
  /* Scrollable content area */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.card-back-content::-webkit-scrollbar {
  width: 6px;
}

.card-back-content::-webkit-scrollbar-track {
  background: transparent;
}

.card-back-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.card-back-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Card Back Elements */
.detail-row-back {
  @apply flex flex-col gap-2;
  animation: fadeInUp 0.4s ease-out;
}

.detail-row-back:nth-child(2) {
  animation-delay: 0.1s;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.detail-label-back {
  @apply text-xs font-bold text-white uppercase tracking-wider;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.detail-value-row-back {
  @apply flex items-center gap-2;
}

.detail-value-back {
  @apply flex-1 text-sm text-gray-900 font-mono;
  @apply px-4 bg-white bg-opacity-95 backdrop-blur-sm rounded-lg;
  @apply shadow-sm;
  @apply flex items-center;
  height: 52px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pin-display-back {
  @apply flex items-center flex-1;
  @apply px-4 bg-white backdrop-blur-sm rounded-lg;
  @apply shadow-sm;
  height: 52px;
}

.pin-emojis-back {
  @apply text-2xl;
  white-space: nowrap;
  letter-spacing: 6px;
}

.btn-action-back {
  @apply flex items-center justify-center;
  @apply w-12 h-12 rounded-lg;
  @apply bg-white bg-opacity-95 backdrop-blur-sm text-purple-600;
  @apply transition-all duration-200;
  @apply cursor-pointer shadow-sm;
  @apply flex-shrink-0;
}

.btn-action-back:hover {
  @apply bg-white bg-opacity-100;
  @apply shadow-md;
  transform: scale(1.05);
}

.btn-action-back.btn-copied {
  @apply bg-green-500 text-white;
}

.btn-action-back.btn-open {
  @apply text-blue-600;
}

.btn-action-back.btn-open:hover {
  @apply text-blue-700;
}

.btn-action-back:active {
  transform: scale(0.95);
}

/* Normal Mode Layout */
.claim-instructions-card {
  @apply w-full flex flex-col gap-5;
}

/* Unified Grid Spacing */
.claim-instructions-card > * {
  @apply rounded-xl;
}

/* Animated Security Warning */
.security-warning-animated {
  @apply bg-gradient-to-r from-orange-50 to-red-50;
  @apply border-2 border-orange-300;
  @apply p-4;
  @apply flex items-center gap-4;
  @apply shadow-sm;
  animation: fadeInSlide 0.6s ease-out;
}

.warning-emoji {
  @apply text-4xl flex-shrink-0;
  animation: pulse-bounce 2s ease-in-out infinite;
  transform-origin: center;
}

.warning-text {
  @apply text-sm font-medium text-gray-800 leading-relaxed m-0;
  animation: fadeIn 0.8s ease-out 0.2s both;
}

/* Keyframe Animations */
@keyframes pulse-bounce {
  0%, 100% {
    transform: scale(1);
  }
  10% {
    transform: scale(1.15) rotate(-5deg);
  }
  20% {
    transform: scale(1.1) rotate(5deg);
  }
  30% {
    transform: scale(1.15) rotate(-5deg);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Gift Card Details Card */
.gift-card-details {
  @apply border-2 border-gray-200 bg-white p-4;
  @apply flex flex-col gap-4;
}

.details-header {
  @apply flex items-center gap-2 pb-3 border-b border-gray-200;
}

.header-icon {
  @apply text-2xl;
}

.header-text {
  @apply text-base font-semibold text-gray-900;
}

.detail-row {
  @apply flex flex-col gap-2;
}

.detail-label {
  @apply text-xs font-medium text-gray-600 uppercase tracking-wide;
}

.detail-value-row {
  @apply flex items-center gap-2;
}

.detail-value {
  @apply flex-1 text-sm text-gray-800 font-mono;
  @apply px-3 py-2 bg-gray-50 rounded-lg;
  @apply border border-gray-200;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pin-display {
  @apply flex items-center justify-center flex-1;
  @apply px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg;
}

.pin-emojis {
  @apply text-xl;
  white-space: nowrap;
  letter-spacing: 4px;
}

.btn-copy-inline {
  @apply flex items-center justify-center;
  @apply w-10 h-10 rounded-lg;
  @apply bg-purple-500 text-white;
  @apply transition-all duration-200;
  @apply cursor-pointer;
  @apply flex-shrink-0;
}

.btn-copy-inline:hover {
  @apply bg-purple-600;
  transform: translateY(-2px);
  @apply shadow-lg;
}

.btn-copy-inline.btn-copied {
  @apply bg-green-500;
}

.btn-copy-inline:active {
  transform: translateY(0);
}

/* Backup Section */
.backup-section {
  @apply bg-blue-50 border-2 border-blue-200 p-4;
  @apply flex flex-col gap-3;
}

.backup-header {
  @apply flex items-center gap-2;
}

.backup-icon {
  @apply text-xl;
}

.backup-title {
  @apply text-base font-semibold text-gray-900;
}

.backup-description {
  @apply text-sm text-gray-700 m-0;
}

.backup-actions {
  @apply grid grid-cols-2 gap-2.5;
}

.btn-backup {
  @apply flex items-center justify-center gap-2;
  @apply px-4 py-3 rounded-lg;
  @apply font-medium text-sm;
  @apply transition-all duration-200;
  @apply cursor-pointer;
  @apply border-2;
}

.btn-copy-all {
  @apply bg-purple-500 border-purple-500 text-white;
}

.btn-copy-all:hover {
  @apply bg-purple-600 border-purple-600;
  transform: translateY(-2px);
  @apply shadow-lg;
}

.btn-copy-all.btn-copied {
  @apply bg-green-500 border-green-500;
}

.btn-save-device {
  @apply bg-blue-500 border-blue-500 text-white;
}

.btn-save-device:hover {
  @apply bg-blue-600 border-blue-600;
  transform: translateY(-2px);
  @apply shadow-lg;
}

.btn-backup:active {
  transform: translateY(0);
}

/* Info Section */
.info-section {
  @apply flex flex-col gap-3 p-4 bg-blue-50 border-2 border-blue-200;
}

.info-text {
  @apply text-xs text-gray-700 leading-relaxed m-0;
}

/* Preview Button */
.btn-preview {
  @apply flex items-center justify-center gap-2 px-4 py-2.5;
  @apply bg-white border-2 border-blue-300 rounded-lg;
  @apply text-sm font-medium text-blue-700;
  @apply cursor-pointer transition-all duration-200;
}

.btn-preview:hover {
  @apply bg-blue-50 border-blue-400;
  transform: translateY(-1px);
}

.btn-preview:active {
  transform: translateY(0);
}

/* Confirmation Section */
.confirmation-section {
  @apply border-2 border-gray-200 p-4;
  @apply bg-white;
}

.checkbox {
  @apply w-5 h-5 rounded cursor-pointer;
  @apply border-2 border-gray-300;
  @apply transition-colors duration-200;
}

.checkbox:checked {
  @apply bg-blue-500 border-blue-500;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .card-back-content {
    @apply p-4 gap-4;
  }

  .detail-label-back {
    @apply text-xs;
  }

  .detail-value-back {
    @apply text-xs px-3;
    height: 44px;
  }

  .pin-display-back {
    @apply px-3;
    height: 44px;
  }

  .pin-emojis-back {
    @apply text-xl;
    letter-spacing: 4px;
  }

  .btn-action-back {
    @apply w-10 h-10;
  }

  .security-warning-animated {
    @apply p-3 gap-3;
  }

  .warning-emoji {
    @apply text-3xl;
  }

  .warning-text {
    @apply text-xs;
  }

  .gift-card-details {
    @apply p-3 gap-3;
  }

  .header-icon {
    @apply text-xl;
  }

  .header-text {
    @apply text-sm;
  }

  .detail-label {
    @apply text-xs;
  }

  .detail-value {
    @apply text-xs px-2 py-1.5;
  }

  .pin-display {
    @apply px-2 py-1;
  }

  .pin-emojis {
    @apply text-base;
    letter-spacing: 3px;
  }

  .btn-copy-inline {
    @apply w-9 h-9;
  }

  .backup-section {
    @apply p-3;
  }

  .backup-icon {
    @apply text-lg;
  }

  .backup-title {
    @apply text-sm;
  }

  .backup-description {
    @apply text-xs;
  }

  .btn-backup {
    @apply px-3 py-2.5 text-xs;
  }

  .btn-backup span {
    @apply text-xs;
  }

  .btn-preview {
    @apply text-xs px-3 py-2;
  }
}
</style>
